// Package imports:
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { SwiperSlide } from 'swiper/react';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons'
// Components imports:
import Slider from '../Slider/Slider';
import FullScreenImage from '../../ui-elements/FullScreenImage/FullScreenImage';
import TextImageBlock from '../TextImageBlock/TextImageBlock';
import PriceCard from '../PriceCard/PriceCard';
import Button from '../../ui-elements/Button/Button';
import { IOptionProps } from '../../ui-elements/DropSelect/DropSelect';
import TandemTable from '../../ui-elements/Table/TandemTable';
import Link from '../../ui-elements/Link/Link';
import HeadSection from '../../ui-elements/HeadSection/HeadSection';
// Service imports:
import { formatNumber, gtmPush } from '../../services/utils';
// Type imports:
import { IPriceInfo } from '../../types/SignUpTypes';


const KELDAN_APP_IMAGES_URLS = [
    "https://cdn.livemarketdata.com/KeldanImages/KeldanAppExample1.png",
    "https://cdn.livemarketdata.com/KeldanImages/KeldanAppExample2.png",
    "https://cdn.livemarketdata.com/KeldanImages/KeldanAppExample3.png",
    "https://cdn.livemarketdata.com/KeldanImages/KeldanAppExample4.png",
    "https://cdn.livemarketdata.com/KeldanImages/KeldanAppExample5.png",
    "https://cdn.livemarketdata.com/KeldanImages/KeldanAppExample6.png",
    "https://cdn.livemarketdata.com/KeldanImages/KeldanAppExample7.png",
    "https://cdn.livemarketdata.com/KeldanImages/KeldanAppExample8.png",
    "https://cdn.livemarketdata.com/KeldanImages/KeldanAppExample9.png",
    "https://cdn.livemarketdata.com/KeldanImages/KeldanAppExample10.png",
    "https://cdn.livemarketdata.com/KeldanImages/KeldanAppExample11.png",
    "https://cdn.livemarketdata.com/KeldanImages/KeldanAppExample12.png",
    "https://cdn.livemarketdata.com/KeldanImages/KeldanAppExample13.png",
    "https://cdn.livemarketdata.com/KeldanImages/KeldanAppExample14.png",
    "https://cdn.livemarketdata.com/KeldanImages/KeldanAppExample15.png",
    "https://cdn.livemarketdata.com/KeldanImages/KeldanAppExample16.png"
]

export const APP_PRICES: {individuals: IPriceInfo[], companies: IPriceInfo[]} = {
    individuals: [{
        id: 'individual_sub',
        description: <span>Mánaðargjald Keldu appsins + val um eina áskriftarleið *</span>,
        price: 3280,
        currency: 'kr.'
    }, {
        id: 'individual_1',
        name: <span><strong>Leið 1:</strong></span>,
        description: <span>Hlutabréf og skuldabréf - Hagstæðasta kaup- og sölutilboð</span>,
        price: 1,
        currency: '€'
    }, {
        id: 'individual_5',
        name: <span><strong>Leið 2:</strong></span>,
        description: <span>Hlutabréf og skuldabréf - Hagstæðustu 5 kaup- og sölutilboð</span>,
        price: 5,
        currency: '€'
    }, {
        id: 'individual_all',
        name: <span><strong>Leið 3:</strong></span>,
        description: <span>Hlutabréf og skuldabréf - Öll kaup- og sölutilboð</span>,
        price: 10,
        currency: '€'
    }],
    companies: [{
        id: 'company_sub',
        description: <span>Mánaðargjald Keldu appsins + val um eina eða fleiri áskriftarleiðir *</span>,
        price: 3280,
        currency: 'kr.'
    }, {
        id: 'company_shares_1',
        name: <span><strong>Leið 1:</strong></span>,
        description: <span>Hlutabréf - Hagstæðasta kaup- og sölutilboð</span>,
        price: 56,
        currency: '€'
    }, {
        id: 'company_shares_5',
        name: <span><strong>Leið 2:</strong></span>,
        description: <span>Hlutabréf - Hagstæðustu 5 kaup</span>,
        price: 98,
        currency: '€'
    }, {
        id: 'company_shares_all',
        name: <span><strong>Leið 3:</strong></span>,
        description: <span>Hlutabréf - Öll kaup- og sölutilboð</span>,
        price: 125,
        currency: '€'
    }, {
        id: 'company_bonds_all',
        name: <span><strong>Leið 4:</strong></span>,
        description: <span>Skuldabréf - Öll kaup- og sölutilboð</span>,
        price: 75,
        currency: '€'
    }]
};

const individualOptions: IOptionProps[] = [];
for (let entry of APP_PRICES.individuals) {
    if (entry.id !== 'individual_sub')
        individualOptions.push({value: entry.id, label: <>{entry.name} {entry.description}, {formatNumber(entry.price, null, 0)} {entry.currency}</>});
}
const companyOptions: IOptionProps[] = [];
for (let entry of APP_PRICES.companies) {
    if (entry.id !== 'company_sub')
        companyOptions.push({id: entry.id, value: entry.id, label: <>{entry.name} {entry.description}, {formatNumber(entry.price, null, 0)} {entry.currency}</>});
}

const KeldanAppPage: React.FC = () => {
    const [ fullScreenImageUrl, setFullScreenImageUrl ] = useState<string | null>(null);

    return (
        <div className="main KCL_keldan-app-page">
            {fullScreenImageUrl && (
                <FullScreenImage
                    imgUrl={fullScreenImageUrl}
                    imgAlt="Fullscreen Keldan App screenshot"
                    onExit={() => setFullScreenImageUrl(null)}
                />
            )}
            <div className="main__inner-fluid grid-items">
                <HeadSection
                    title={<object data="https://cdn.livemarketdata.com/KeldanImages/KeldanAppLogo.svg" aria-label="Keldan App">Keldan App</object>}
                    description={<p>Vertu með viðskiptalífið í vasanum!</p>}
                    textAlign='center'
                />
                <div className="section__body">
                    <div className="shell">
                        <TextImageBlock
                            imgSrc="https://cdn.livemarketdata.com/KeldanImages/KeldanApp.png"
                            imgSide="right"
                        >
                            <h2 id="KeldanApp_Summary">Hlutabréf og gjaldmiðlar í rauntíma</h2>
                            <div className="text-image-block__entry">
                                <p>Keldan App er farsímaforrit með Premium áskrift sem býður upp á rauntímaupplýsingar um gengi hlutabréfa, gjaldmiðla og vísitalna, ásamt Vaktinni sem er verðbréfavakt í rauntíma.</p>
                                <p>Einnig er hægt að fylgjast með viðskiptafréttunum um leið og þær gerast. Keldan App safnar saman viðskiptafréttum, innlendum sem erlendum, frá öllum helstu fréttaveitum landsins.</p>
                                <p>Appið er fáanlegt fyrir bæði iPhone og Android síma.</p>
                            </div>
                        </TextImageBlock>
                    </div>
                </div>
                <div id='Askriftarleidir' className="section section--gray section--subscription">
                    <div className="section__head">
                        <h2 id="KeldanApp_Subscriptions">Áskriftarleiðir</h2>
                        <p>Keldan býður upp á nokkrar áskriftarleiðir í appinu til að mæta ólíkum þörfum viðskiptavina.</p>
                    </div>
                    <div className="section__body">
                        <div className="shell">
                            <Slider
                                perView={3}
                                centerSlides={true}
                                loop={false}
                                hideActions={true}
                                initialSlide={1}
                                breakpointOptions={{
                                    320: {
                                        slidesPerView: 1,
                                        noSwiping: false
                                    },
                                    768: {
                                        slidesPerView: 3,
                                        noSwiping: true
                                    }
                                }}
                                onSlideChange={() => {}}
                                onInit={() => {}}
                                className="price-cards__slider swiper-no-swiping"
                            >
                                <SwiperSlide>
                                    {/* FIRST PRICE CARD */}
                                    <PriceCard
                                        type="Fría útgáfan"
                                        key="basic"
                                    >
                                        <p><strong>Frítt í Keldan App</strong></p>
                                        <ul className="list-with-checks">
                                            <li><FontAwesomeIcon icon={faCheck} className="icon" /> 15 mínútna seinkun á markaðsgögnum.</li>
                                            <li><FontAwesomeIcon icon={faCheck} className="icon" /> Viðskiptafréttir.</li>
                                        </ul>
                                    </PriceCard>
                                </SwiperSlide>
                                <SwiperSlide>
                                    {/* SECOND PRICE CARD */}
                                    <PriceCard
                                        variant="primary"
                                        type="Premium áskrift"
                                        key="individual"
                                    >
                                        <p><strong>Aðeins fyrir einstaklinga</strong></p>
                                        <div className='storePriceContainer'>
                                            <div className='storePriceWrapper'>
                                                <span className='storePriceWrapper__price'><strong>19.83</strong></span>
                                                <span className='storePriceWrapper__currency'>USD á mánuði hjá</span>
                                                <span className='storePriceWrapper__store'><strong>App Store</strong></span>
                                            </div>
                                            {/* Vertical line */}
                                            <div className='vertical_line'></div>
                                            <div className='storePriceWrapper'>
                                                <span className='storePriceWrapper__price'><strong>19.99</strong></span>
                                                <span className='storePriceWrapper__currency'>USD á mánuði hjá</span>
                                                <span className='storePriceWrapper__store'><strong>Google Play</strong></span>
                                            </div>
                                        </div>
                                        <ul className="list-with-checks">
                                            <li><FontAwesomeIcon icon={faCheck} className="icon" /> Markaðsgögn í rauntíma.</li>
                                            <li><FontAwesomeIcon icon={faCheck} className="icon" /> Yfirlit yfir viðskipti.</li>
                                            <li><FontAwesomeIcon icon={faCheck} className="icon" /> Tilkynningar og rauntímavakt á hlutabréf að eigin vali.</li>
                                        </ul>
                                    </PriceCard>
                                </SwiperSlide>
                                <SwiperSlide>
                                    {/* THIRD PRICE CARD */}
                                    <PriceCard
                                        variant="secondary"
                                        type="Gull áskrift"
                                        key="individual"
                                        noPriceLabel
                                        KeldanAppPriceCard
                                    >   
                                        <p><strong>Fyrir fyrirtæki og einstaklinga</strong></p>
                                        <div className="price-card__price">
                                            <p>
                                                <strong>3.280</strong>
                                                <span>kr. á mánuði + VSK</span>
                                            </p>
                                        </div>
                                        <p><strong>Ásamt mánaðargjaldi Nasdaq OMX*</strong></p>
                                        <ul className="list-with-checks">
                                            <li><FontAwesomeIcon icon={faCheck} className="icon" /> Markaðsgögn í rauntíma.</li>
                                            <li><FontAwesomeIcon icon={faCheck} className="icon" /> Yfirlit yfir viðskipti.</li>
                                            <li><FontAwesomeIcon icon={faCheck} className="icon" /> Tilkynningar og rauntímavakt.</li>
                                            <li><FontAwesomeIcon icon={faCheck} className="icon" /> Upplýsingar um öll verð í tilboðabókum hlutabréfa og skuldabréfa.</li>
                                        </ul>
                                        <div className="price-card__actions">
                                            <Button
                                                buttonType="primary"
                                                anchorProps={{
                                                    href: `/Keldan-App/Hafa-Samband`
                                                }}
                                            >
                                                Koma í áskrift
                                            </Button>
                                        </div>
                                    </PriceCard>
                                    <div className="price-card-notice italic-text">
                                        <p>*Sjá verðskrá fyrir neðan.</p>
                                    </div>
                                </SwiperSlide>
                            </Slider>
                            {/* APP STORE SECTION */}
                            <div className='section--subscription section--appStoreLinks'>
                                <p><strong>Þú sækir Keldan Appið hér:</strong></p>
                                <div className="app-download-links">
                                    <a
                                        className="google-play"
                                        href="https://play.google.com/store/apps/details?id=is.kodi.keldanMobile"
                                        target='_blank'
                                        rel='noreferrer'
                                        onClick={() => gtmPush('buttonClick', 'download_app', 'link_to_store', 'google_play')}
                                        aria-label="Keldu appið á Google Play Store"
                                    >
                                        <img
                                            src='https://cdn.livemarketdata.com/Images/google-play.png'
                                            alt="Google play store logo"
                                            width="132"
                                            height="39"
                                        />
                                    </a>
                                    <a
                                        className="app-store"
                                        href="https://apps.apple.com/is/app/keldan-app/id1162095731"
                                        target='_blank'
                                        rel='noreferrer'
                                        onClick={() => gtmPush('buttonClick', 'download_app', 'link_to_store', 'app_store')}
                                        aria-label="Keldu appið á Apple App Store"
                                    >
                                        <img
                                            src='https://cdn.livemarketdata.com/Images/app-store.png'
                                            alt="Apple app store logo"
                                            width="132"
                                            height="44"
                                        />
                                    </a>
                                </div>
                                <div className="price-card-notice appStoreNoticeText">
                                    <p>Til að virkja Premium áskrift eftir að appið er sótt skal <br/>fara í stillingar inn í appinu og smella á <br/> ”Fá Keldan App Premium”.</p>
                                </div>
                                <Link url='/Keldan-App/Skilmalar' linkSize='16'><FontAwesomeIcon icon={faAngleRight}/> Lesa skilmála Keldan App</Link>
                                
                            </div>
                            
                        </div>
                    </div>
                </div>
                <div id='Verdskra' className="section section--primary price-table-section">
                    <div className="section__head left-align">
                        <div className="shell">
                            <h2 id="KeldanApp_Prices">Verðskrá fyrir Gull áskrift</h2>
                            <p className="italic-text">Öll verð eru án vsk. og samkvæmt gjaldskrá Nasdaq OMX fyrir aðgang einstaklinga og fyrirtækja að rauntímagögnum. </p>
                        </div>
                    </div>
                    <div className="section__body">
                        <div className="shell">
                            <TandemTable
                                pricingTableHeaderStyle
                                data={[APP_PRICES.individuals, APP_PRICES.companies]}
                                columns={[[{
                                    title: <span className="price-table-category">Einstaklingar</span>,
                                    dataAccessor: ({ name, description }) => <>{name} {description}</>,
                                    textAlign: 'left'
                                }, {
                                    title: 'Verð á mán.',
                                    dataAccessor: ({ price, currency }) => <>{formatNumber(price, null, 0)} {currency}</>,
                                    textAlign: 'right'
                                }], [{
                                    title: <span className="price-table-category">Fyrirtæki</span>,
                                    dataAccessor: ({ name, description }) => <>{name} {description}</>,
                                    textAlign: 'left'
                                }, {
                                    title: <span className="empty-span"></span>,
                                    dataAccessor: ({ price, currency }) => <>{formatNumber(price, null, 0)} {currency}</>,
                                    textAlign: 'right'
                                }]]}
                                tableSize="lg"
                            />
                        </div>
                    </div>
                </div>
                <div className="section screenshotSection">
                    <div className="section__head">
                        <div className="shell">
                            <h2 id="KeldanApp_Screenshots">Skjáskot úr appinu</h2>
                        </div>
                    </div>
                    <div className="section__body">
                        <div className="shell">
                            <Slider
                                className="slider-screenshots"
                                perView={5}
                                centerSlides={false}
                                loop={true}
                                breakpointOptions={{
                                    320: {
                                        slidesPerView: 1
                                    },
                                    768: {
                                        slidesPerView: 3
                                    },
                                    1440: {
                                        slidesPerView: 5
                                    }
                                }}
                                onInit={() => {}}
                                onSlideChange={() => {}}
                            >
                                {KELDAN_APP_IMAGES_URLS.map(url => (
                                    <SwiperSlide
                                        key={url}
                                        onClick={() => setFullScreenImageUrl(url)}
                                    >
                                        <div>
                                            <img
                                                src={url}
                                                alt="Keldan App screenshot"
                                                className='slide-image'
                                                width="1080"
                                                height="2340"
                                            />
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Slider>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default KeldanAppPage;
